import React from 'react';
import { useInView } from '@/hooks/useInView';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck, Unlock, DollarSign, Rocket, BarChart3, MessageCircle } from 'lucide-react';

const SERVICES = [
  {
    icon: ClipboardCheck,
    title: "Smooth Onboarding",
    description: "I keep the setup process simple, organized, and quick while still taking the time to build things the right way."
  },
  {
    icon: Unlock,
    title: "No Lock Ins",
    description: "Work with me because the results make sense, not because you're stuck in a contract. It's all month to month, totally flexible, and pressure free."
  },
  {
    icon: DollarSign,
    title: "Straightforward Pricing",
    description: "No surprises, no weird add ons. Just clear pricing based on what your account needs and what you're trying to achieve."
  },
  {
    icon: Rocket,
    title: "Full Build & Launch",
    description: "I handle the whole setup for you: landing pages, keyword research, ad copy, extensions, tracking, the works. Everything is built to convert from day one so you can hit the ground running."
  },
  {
    icon: BarChart3,
    title: "Ongoing Tuning & Improvements",
    description: "Your campaigns get better over time because I'm constantly adjusting, testing, and refining based on real data. No 'set it and forget it' here, just steady, meaningful improvements."
  },
  {
    icon: MessageCircle,
    title: "Open Communication",
    description: "You'll always know what's happening with your budget and what results you're getting. Every call, form fill, and sale is tracked and explained in plain English. No fluff, just clarity."
  }
];

export const ServicesSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section data-testid="services-section" className="py-16 lg:py-24 bg-background" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-3">Services</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
            Everything You Need to Win With Google Ads
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <Card
                key={i}
                className={`bg-card border-border hover:shadow-[var(--shadow-elevated)] hover:-translate-y-1 flex flex-col transition-all duration-300 ${
                  isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${150 + i * 100}ms` }}
              >
                <CardHeader className="pb-3">
                  <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <CardTitle className="text-foreground text-lg">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
